/* eslint-disable camelcase */

import request from 'utils/request';
import { each, get, isArray, isObject } from 'lodash';
import { isAuthenticated } from 'utils/authentication';
import Validator from 'validatorjs';

/**
 * Adds item to cart using the item_id (package, typically) and quantity
 * @param  {Number} item_id    	Id of item requested
 * @param  {Number} quantity 	CPU count
 * @return {Promise}
 */
export function addToCart(body) {
	// Switch endpoints depending on single or multiple items being added
	let data = body;
	let endpoint;

	// Item is an app license
	if (data.type && data.type === 'app' && data.license_term) {
		endpoint = '/api/v1/cart_items/subscription';
		data = { body: data };
	// Item has multiple items (as with custom packages)
	} else if (typeof data.items === 'string') {
		endpoint = '/api/v1/cart_items/mass_add';
		data = { body: { items: data.items } };
	} else {
		endpoint = '/api/v1/cart_items';
		data = { body: data };
	}

	return request(endpoint, {
		authenticate: false,
		method: 'POST',
		...data
	});
}

/**
 * Removes item from cart using the item_id
 * @param  {Number} item_id    	Id of item requested
 * @return {Promise}
 */
export function removeFromCart({ id }) {
	return request(`/api/v1/cart_items/${id}`, {
		authenticate: false,
		method: 'DELETE'
	});
}

/**
 * Fetch cart for current user
 * @return {Promise}
 */
export function getCart({ showReconciliation = false, cartItems = true } = false) {
	return request('/api/v1/cart', {
		authenticate: isAuthenticated(),
		params: {
			// TODO: Update request to exclude `include` param when able. This
			// will require the base `/cart` endpoint to include an array with
			// each license type listed that's in the cart.
			// include: 'cart_items,discounts',
			include: cartItems && 'cart_items,discounts,ctas,promo_codes',
			serializer: 'array',
			show_reconciliation: showReconciliation
		}
	});
}

/**
 * Updates multiple items in the cart using cartRecordID
 * @param  {Number} id(s)    	One or more cart IDs to update
 * @param  {Object} data    	Data object containing key/value pairs for
 *                           	columns to update
 * @return {Promise}
 */
export function updateCartItems({ ids, data }) {
	// If `ids` is an array/object, send the request to the
	// appropriate endpoint
	if (isObject(ids)) {
		return request('/api/v1/cart_items/update_cart_items', {
			authenticate: true,
			method: 'PUT',
			body: {
				cart_item_ids: ids,
				quantity: data.quantity
			}
		});
	}

	// Otherwise update a single record
	return request(`/api/v1/cart_items/${ids}`, {
		authenticate: true,
		method: 'PUT',
		body: data
	});
}

/**
 * Define schema for all possible cart items. Used to ensure only the data
 * that’s expected is sent.
 *
 * @method  cartSchema
 * @param   {Object}  data  Object with all necessary data to add this item to the cart
 * @return  {Object}  Schema for the cart item type that was passed in
 */
export function cartSchema(type) {
	switch (type) {
		case 'cloud':
			return {
				cloud_subscription_id: 'numeric|required',
				item_id: 'numeric|required'
			};
		case 'app':
			return {
				item_id: 'numeric|required'
			};
		case 'computer':
			return {
				item_id: 'numeric|required',
				quantity: 'numeric|required'
			};
		case 'selfHosted': return {};
		default: return {};
	}
}

/**
 * Check the cart data to ensure the requested cart item type’s data matches
 * the expected format.
 *
 * @method  cartDataIsValid
 * @param   {String}  type  The type of item we’re trying to add
 * @param   {Object}  data  The item’s data
 * @return  {Boolean}
 */
function cartDataIsValid(type, data) {
	const schema = cartSchema(type);
	const validation = new Validator(data, schema);

	let isValid;
	validation.passes(() => {
		isValid = { valid: true };
	});
	validation.fails(() => {
		isValid = {
			valid: false,
			error: validation.errors.all()
		};
	});

	return isValid;
}

/**
 * Take the cart item data and pull only what’s needed for the request. This uses internal data and
 * formats the data as the API expects it to be formatted.
 *
 * @method  extractCartData
 * @param   {Object}  data  The full dataset for this item
 * @return  {Object}  Formatted object to send on to the API
 *
 * Data will come in using this structure:
 * 		{
 * 			item_id: 100100,
 * 			item_type: 'cloud', (cloud, app, self_hosted)
 * 			quantity: 10, (only used with computer licenses)
 * 			value: 1 (cloud plan id; app plan id; self-hosted plan)
 * 		}
 */
export function extractCartData(data) {
	// Get the `type` from the data. Should be cloud, app, web font, or computer
	const type = get(data, 'item_type');

	let item_id = get(data, 'item_id');
	if (!isArray(item_id)) { item_id = parseInt(get(data, 'item_id'), 10); }

	const license_term = get(data, 'license_term', 1);
	const app_title_count = get(data, 'app_title_count', 1);

	let cleanData;
	switch (type) {
		case 'cloud':
			cleanData = {
				cloud_subscription_id: get(data, 'value'),
				item_id
			};
			break;

		case 'app':
			cleanData = {
				app_title_count,
				item_id,
				license_term,
				type
			};
			break;

		case 'selfHosted':
			if (isArray(item_id)) {
				const items = [];
				each(item_id, (itemID) => {
					items.push({
						item_id: itemID,
						self_hosted_pageview_id: parseInt(data.self_hosted_pageview_id, 10)
					});
				});
				cleanData = items;
			} else {
				cleanData = {
					item_id,
					self_hosted_pageview_id: parseInt(data.self_hosted_pageview_id, 10)
				};
			}
			break;

		case 'computer':
		default: {
			// If a number (ID) was sent, add a single cart item
			if (typeof item_id === 'number' && item_id > 0) {
				cleanData = {
					item_id,
					quantity: parseInt(get(data, 'quantity'), 10)
				};
			// If an object was sent, create a JSON string with all IDs
			} else if (isArray(item_id)) {
				const items = [];
				each(item_id, (itemID) => {
					items.push({
						item_id: itemID,
						quantity: get(data, 'licenses.computer.value')
					});
				});
				cleanData = items;
			}
		}
	}

	// Ensure that the values are typed properly.
	const isValid = cartDataIsValid(type, cleanData).valid;

	// … and return
	if (isValid) {
		return cleanData;
	}

	return {};
}

export default {
	addToCart,
	cartDataIsValid,
	extractCartData,
	getCart,
	removeFromCart,
	updateCartItems
};
